import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  ArrowLeft, 
  Search, 
  Plus, 
  Trash2, 
  Edit3, 
  BookOpen,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Database,
  FileText,
  Loader2,
  X,
  Check,
  ExternalLink,
  Rss,
  Save,
  Clock,
  Download,
  Bot,
  RotateCcw,
  Music
} from 'lucide-react';
import { toast } from 'sonner';
import SongsByThemeManager from './SongsByThemeManager';
import { useDesign } from '@/contexts/DesignContext';
import StudioToolHeader, { ToolSearch } from '@/components/studio/admin/StudioToolHeader';
import { useChromeSlots } from '@/components/studio/admin/chromeSlots';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function KnowledgeBaseManager({ onClose, userEmail }) {
  const { isStudio } = useDesign();
  const { slots, claim, Provider: SlotProvider } = useChromeSlots();
  const adminQS = `user_email=${encodeURIComponent(userEmail || '')}`;
  // Tab state
  const [activeTab, setActiveTab] = useState('articles');
  
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  
  // Dialog states
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [fullArticle, setFullArticle] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    url: '',
    categories: ''
  });
  const [saving, setSaving] = useState(false);
  const [reimporting, setReimporting] = useState(false);
  
  // RSS Config state
  const [rssConfig, setRssConfig] = useState({
    rss_url: '',
    default_url: '',
    last_fetch: null,
    last_fetch_count: 0
  });
  const [rssUrlInput, setRssUrlInput] = useState('');
  const [loadingRss, setLoadingRss] = useState(true);
  const [savingRss, setSavingRss] = useState(false);
  const [fetchingRss, setFetchingRss] = useState(false);
  const [showRssConfig, setShowRssConfig] = useState(false);

  // System Prompt state
  const [systemPrompt, setSystemPrompt] = useState('');
  const [originalSystemPrompt, setOriginalSystemPrompt] = useState('');
  const [defaultSystemPrompt, setDefaultSystemPrompt] = useState('');
  const [isCustomPrompt, setIsCustomPrompt] = useState(false);
  const [promptUpdatedAt, setPromptUpdatedAt] = useState(null);
  const [loadingPrompt, setLoadingPrompt] = useState(true);
  const [savingPrompt, setSavingPrompt] = useState(false);
  const [showPromptConfig, setShowPromptConfig] = useState(false);

  // Fetch System Prompt config
  const fetchSystemPrompt = async () => {
    setLoadingPrompt(true);
    try {
      const response = await fetch(`${API_URL}/api/ai/system-prompt`);
      const data = await response.json();
      setSystemPrompt(data.system_prompt || '');
      setOriginalSystemPrompt(data.system_prompt || '');
      setDefaultSystemPrompt(data.default_prompt || '');
      setIsCustomPrompt(data.is_custom || false);
      setPromptUpdatedAt(data.updated_at);
    } catch (err) {
      console.error('Error fetching system prompt:', err);
      toast.error('Failed to load system prompt');
    } finally {
      setLoadingPrompt(false);
    }
  };

  const handleSaveSystemPrompt = async () => {
    setSavingPrompt(true);
    try {
      const response = await fetch(`${API_URL}/api/ai/system-prompt?${adminQS}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ system_prompt: systemPrompt })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to save');
      }
      
      setOriginalSystemPrompt(systemPrompt);
      setIsCustomPrompt(true);
      setPromptUpdatedAt(new Date().toISOString());
      toast.success('System prompt saved for all churches!');
    } catch (err) {
      console.error('Error saving system prompt:', err);
      toast.error(err.message || 'Failed to save system prompt');
    } finally {
      setSavingPrompt(false);
    }
  };

  const handleResetSystemPrompt = async () => {
    if (!window.confirm('Are you sure you want to reset the system prompt to default? This will affect ALL churches.')) {
      return;
    }
    
    setSavingPrompt(true);
    try {
      const response = await fetch(`${API_URL}/api/ai/system-prompt/reset?${adminQS}`, {
        method: 'POST'
      });
      
      if (!response.ok) throw new Error('Failed to reset');
      
      setSystemPrompt(defaultSystemPrompt);
      setOriginalSystemPrompt(defaultSystemPrompt);
      setIsCustomPrompt(false);
      setPromptUpdatedAt(null);
      toast.success('System prompt reset to default for all churches');
    } catch (err) {
      console.error('Error resetting system prompt:', err);
      toast.error('Failed to reset system prompt');
    } finally {
      setSavingPrompt(false);
    }
  };

  // Fetch RSS config
  const fetchRssConfig = async () => {
    setLoadingRss(true);
    try {
      const response = await fetch(`${API_URL}/api/ai/rss-config`);
      const data = await response.json();
      setRssConfig(data);
      setRssUrlInput(data.rss_url || '');
    } catch (err) {
      console.error('Error fetching RSS config:', err);
    } finally {
      setLoadingRss(false);
    }
  };

  // Save RSS URL
  const handleSaveRssUrl = async () => {
    if (!rssUrlInput.trim()) {
      toast.error('RSS URL is required');
      return;
    }

    setSavingRss(true);
    try {
      const response = await fetch(`${API_URL}/api/ai/rss-config?${adminQS}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rss_url: rssUrlInput })
      });

      if (response.ok) {
        toast.success('RSS URL saved');
        fetchRssConfig();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to save RSS URL');
      }
    } catch (err) {
      console.error('Error saving RSS URL:', err);
      toast.error('Failed to save RSS URL');
    } finally {
      setSavingRss(false);
    }
  };

  // Trigger RSS fetch
  const handleFetchRss = async () => {
    setFetchingRss(true);
    try {
      const response = await fetch(`${API_URL}/api/ai/rss-fetch?${adminQS}`, {
        method: 'POST'
      });
      const data = await response.json();

      if (data.success) {
        toast.success(data.message || `Fetched ${data.new_articles} new articles`);
        fetchRssConfig();
        fetchArticles(1, searchQuery);
      } else {
        toast.error(data.detail || 'Failed to fetch RSS feed');
      }
    } catch (err) {
      console.error('Error fetching RSS:', err);
      toast.error('Failed to fetch RSS feed');
    } finally {
      setFetchingRss(false);
    }
  };

  // Delete confirmation state
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [articleToDelete, setArticleToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Fetch articles
  const fetchArticles = async (pageNum = 1, search = '') => {
    setLoading(true);
    try {
      let url = `${API_URL}/api/ai/knowledge-base/articles?page=${pageNum}&limit=20`;
      if (search) {
        url += `&search=${encodeURIComponent(search)}`;
      }
      
      const response = await fetch(url);
      const data = await response.json();
      
      setArticles(data.articles || []);
      setTotal(data.total || 0);
      setTotalPages(data.total_pages || 1);
      setPage(pageNum);
    } catch (err) {
      console.error('Error fetching articles:', err);
      toast.error('Failed to fetch articles');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchArticles();
    fetchRssConfig();
    fetchSystemPrompt();
  }, []);

  // Search handler with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchArticles(1, searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // View full article
  const handleViewArticle = async (article) => {
    try {
      const response = await fetch(`${API_URL}/api/ai/knowledge-base/article?url=${encodeURIComponent(article.url)}`);
      const data = await response.json();
      setFullArticle(data);
      setShowViewDialog(true);
    } catch (err) {
      console.error('Error fetching article:', err);
      toast.error('Failed to load article');
    }
  };

  // Edit article
  const handleEditArticle = async (article) => {
    try {
      const response = await fetch(`${API_URL}/api/ai/knowledge-base/article?url=${encodeURIComponent(article.url)}`);
      const data = await response.json();
      setSelectedArticle(data);
      setFormData({
        title: data.title || '',
        content: data.content || '',
        url: data.url || '',
        categories: (data.categories || []).join(', ')
      });
      setShowEditDialog(true);
    } catch (err) {
      console.error('Error fetching article:', err);
      toast.error('Failed to load article');
    }
  };

  // Save new article
  const handleAddArticle = async () => {
    if (!formData.title.trim() || !formData.content.trim()) {
      toast.error('Title and content are required');
      return;
    }

    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/ai/knowledge-base/article?${adminQS}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: formData.title,
          content: formData.content,
          url: formData.url || null,
          categories: formData.categories ? formData.categories.split(',').map(c => c.trim()).filter(Boolean) : []
        })
      });

      if (response.ok) {
        toast.success('Article added successfully');
        setShowAddDialog(false);
        setFormData({ title: '', content: '', url: '', categories: '' });
        fetchArticles(1, searchQuery);
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to add article');
      }
    } catch (err) {
      console.error('Error adding article:', err);
      toast.error('Failed to add article');
    } finally {
      setSaving(false);
    }
  };

  // Update article
  const handleUpdateArticle = async () => {
    if (!formData.title.trim() || !formData.content.trim()) {
      toast.error('Title and content are required');
      return;
    }

    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/ai/knowledge-base/article?url=${encodeURIComponent(selectedArticle.url)}&${adminQS}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: formData.title,
          content: formData.content,
          categories: formData.categories ? formData.categories.split(',').map(c => c.trim()).filter(Boolean) : []
        })
      });

      if (response.ok) {
        toast.success('Article updated successfully');
        setShowEditDialog(false);
        setSelectedArticle(null);
        fetchArticles(page, searchQuery);
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to update article');
      }
    } catch (err) {
      console.error('Error updating article:', err);
      toast.error('Failed to update article');
    } finally {
      setSaving(false);
    }
  };

  // Delete article - opens confirmation dialog
  const handleDeleteArticle = (article) => {
    setArticleToDelete(article);
    setShowDeleteDialog(true);
  };

  // Confirm delete
  const confirmDeleteArticle = async () => {
    if (!articleToDelete) return;
    
    setDeleting(true);
    try {
      const response = await fetch(`${API_URL}/api/ai/knowledge-base/article?url=${encodeURIComponent(articleToDelete.url)}&${adminQS}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        toast.success('Article deleted');
        fetchArticles(page, searchQuery);
      } else {
        toast.error('Failed to delete article');
      }
    } catch (err) {
      console.error('Error deleting article:', err);
      toast.error('Failed to delete article');
    } finally {
      setDeleting(false);
      setShowDeleteDialog(false);
      setArticleToDelete(null);
    }
  };

  // Re-import from WordPress XML
  const handleReimport = async () => {
    if (!window.confirm('This will re-import all articles from the WordPress XML file. Existing manual entries will be preserved. Continue?')) {
      return;
    }

    setReimporting(true);
    try {
      const response = await fetch(`${API_URL}/api/ai/knowledge-base/reimport?${adminQS}`, {
        method: 'POST'
      });
      const data = await response.json();

      if (data.success) {
        toast.success('Import completed successfully');
        fetchArticles(1, searchQuery);
      } else {
        toast.error(data.error || 'Import failed');
      }
    } catch (err) {
      console.error('Error reimporting:', err);
      toast.error('Failed to reimport');
    } finally {
      setReimporting(false);
    }
  };

  /* The four article dialogs are shared by both designs: shadcn Dialog and
     AlertDialog already carry the Studio look through §7 data-ui hooks. */
  const studioDialogs = (
    <>
      {/* Add Article Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5 text-purple-500" />
              Add New Article
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label>Title *</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Article title"
              />
            </div>
            <div className="space-y-2">
              <Label>Content *</Label>
              <Textarea
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                placeholder="Article content (plain text)"
                rows={10}
              />
            </div>
            <div className="space-y-2">
              <Label>URL (optional)</Label>
              <Input
                value={formData.url}
                onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                placeholder="https://example.com/article"
              />
            </div>
            <div className="space-y-2">
              <Label>Categories (comma separated)</Label>
              <Input
                value={formData.categories}
                onChange={(e) => setFormData({ ...formData, categories: e.target.value })}
                placeholder="worship, leadership, training"
              />
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setShowAddDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleAddArticle} disabled={saving} className="bg-purple-600 hover:bg-purple-700">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
                Add Article
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Article Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit3 className="w-5 h-5 text-blue-500 dark:text-blue-400" />
              Edit Article
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label>Title *</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Article title"
              />
            </div>
            <div className="space-y-2">
              <Label>Content *</Label>
              <Textarea
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                placeholder="Article content (plain text)"
                rows={10}
              />
            </div>
            <div className="space-y-2">
              <Label>Categories (comma separated)</Label>
              <Input
                value={formData.categories}
                onChange={(e) => setFormData({ ...formData, categories: e.target.value })}
                placeholder="worship, leadership, training"
              />
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setShowEditDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleUpdateArticle} disabled={saving} className="bg-blue-600 hover:bg-blue-700">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
                Save Changes
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* View Article Dialog */}
      <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-purple-500" />
              {fullArticle?.title}
            </DialogTitle>
          </DialogHeader>
          {fullArticle && (
            <div className="pt-4">
              {fullArticle.url && (
                <a 
                  href={fullArticle.url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-sm text-purple-600 dark:text-purple-400 hover:underline flex items-center gap-1 mb-4"
                >
                  <ExternalLink className="w-3 h-3" />
                  {fullArticle.url}
                </a>
              )}
              {fullArticle.categories?.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-4">
                  {fullArticle.categories.map((cat, i) => (
                    <span key={i} className="px-2 py-1 bg-slate-100 dark:bg-[#3a3d41] text-slate-600 dark:text-slate-300 text-xs rounded-full">
                      {cat}
                    </span>
                  ))}
                </div>
              )}
              <div className="prose prose-sm max-w-none dark:prose-invert">
                <p className="whitespace-pre-wrap text-slate-700 dark:text-slate-200">{fullArticle.content}</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Trash2 className="w-5 h-5 text-red-500 dark:text-red-400" />
              Delete Article
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &quot;{articleToDelete?.title}&quot;? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteArticle}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );

  if (isStudio) {
    /* Studio (§31): one chrome for both tabs — back · database glyph · title +
       sub · the header's centred search · Re-import XML and Add article in the
       right cluster · one neutral 44 tab row (mock questions 6 and 7). The
       songs tab's own controls portal into the same header (chromeSlots).
       Mock frames 15-20. */
    const promptDirty = showPromptConfig && systemPrompt !== originalSystemPrompt;
    return (
      <SlotProvider value={slots}>
      <div className="st31-page">
        <StudioToolHeader
          icon={Database}
          title="AI knowledge base"
          sub={activeTab === 'songs' ? <span ref={claim('sub')} /> : `${(total || 0).toLocaleString()} articles · shared by every church`}
          onBack={onClose}
          search={activeTab === 'songs'
            ? <span className="st31-srch" ref={claim('search')} />
            : <ToolSearch value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Search articles by title or content..." />}
          right={activeTab === 'songs' ? <span ref={claim('right')} style={{ display: 'flex', alignItems: 'center', gap: 6 }} /> : (<>
            <button type="button" className="st-btn st-ghost st-sm" onClick={handleReimport} disabled={reimporting}>
              <RefreshCw className={reimporting ? 'st31-spinning' : undefined} /> Re-import XML
            </button>
            <button
              type="button"
              className={`st-btn st-sm ${promptDirty ? 'st-sec' : 'st-pri'}`}
              onClick={() => { setFormData({ title: '', content: '', url: '', categories: '' }); setShowAddDialog(true); }}
            >
              <Plus /> Add article
            </button>
          </>)}
          tabs={[
            { key: 'articles', label: 'Articles', testid: 'articles-tab' },
            { key: 'songs', label: 'Songs by theme', testid: 'songs-tab' },
          ]}
          activeTab={activeTab}
          onTab={setActiveTab}
        />
        <div className="st31-pg">
          {activeTab === 'songs' ? <SongsByThemeManager /> : (
            <div className="st31-stack">
              {/* RSS feed — collapsed to a row, expanded to a panel */}
              <div className="st31-card">
                {!showRssConfig ? (
                  <div className="st31-band">
                    <span className="st31-g36"><Rss /></span>
                    <span className="st31-c">
                      <span className="st31-nm">RSS feed</span>
                      <span className="st31-mt">
                        {rssConfig.last_fetch
                          ? `Last fetch ${new Date(rssConfig.last_fetch).toLocaleString()} · ${rssConfig.last_fetch_count} new articles`
                          : 'Never fetched'}
                      </span>
                    </span>
                    <button type="button" className="st-btn st-sec st-sm" onClick={() => setShowRssConfig(true)} data-testid="toggle-rss-config">Configure</button>
                  </div>
                ) : (<>
                  <div className="st31-ch">
                    <h2 className="st31-ct">RSS feed</h2>
                    <span className="st31-cr">
                      <button type="button" className="st-btn st-ghost st-sm" onClick={() => setShowRssConfig(false)} data-testid="toggle-rss-config">Hide</button>
                    </span>
                  </div>
                  <div className="st31-cb">
                    <div className="st31-field">
                      <label>RSS feed URL{rssUrlInput !== rssConfig.rss_url && <span className="st31-cap"> · unsaved changes</span>}</label>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <Input type="url" value={rssUrlInput} onChange={(e) => setRssUrlInput(e.target.value)} placeholder="https://feeds.feedburner.com/..." className="flex-1" data-testid="rss-url-input" />
                        <button type="button" className="st-btn st-sec st-sm" onClick={handleSaveRssUrl} disabled={savingRss || rssUrlInput === rssConfig.rss_url} data-testid="save-rss-url-btn">
                          {savingRss ? <Loader2 className="st31-spinning" /> : <Save />} Save
                        </button>
                      </div>
                      {rssConfig.default_url && rssUrlInput !== rssConfig.default_url && (
                        <span className="st31-cap">
                          Default: {rssConfig.default_url}
                          <button type="button" className="st-lnk" style={{ marginLeft: 8 }} onClick={() => setRssUrlInput(rssConfig.default_url)}>Reset to default</button>
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="st31-cf" style={{ borderTop: '1px solid var(--st-line)', paddingTop: 12 }}>
                    <span className="st31-cap" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><Clock style={{ width: 14, height: 14 }} /> RSS feeds are automatically fetched monthly</span>
                    <span className="st31-fsp" />
                    <button type="button" className="st-btn st-ghost st-sm" onClick={handleFetchRss} disabled={fetchingRss} data-testid="fetch-rss-btn">
                      {fetchingRss ? <Loader2 className="st31-spinning" /> : <Download />} Fetch now
                    </button>
                  </div>
                </>)}
              </div>

              {/* AI system prompt — collapsed to a row, expanded to a panel */}
              <div className="st31-card">
                {!showPromptConfig ? (
                  <div className="st31-band">
                    <span className="st31-g36"><Bot /></span>
                    <span className="st31-c">
                      <span className="st31-nm">AI system prompt</span>
                      <span className="st31-mt">
                        {isCustomPrompt
                          ? `Custom prompt active${promptUpdatedAt ? ` · updated ${new Date(promptUpdatedAt).toLocaleDateString()}` : ''}`
                          : 'Using default prompt'}
                      </span>
                    </span>
                    <button type="button" className="st-btn st-sec st-sm" onClick={() => setShowPromptConfig(true)} data-testid="toggle-prompt-config">Edit prompt</button>
                  </div>
                ) : (<>
                  <div className="st31-ch">
                    <h2 className="st31-ct">AI system prompt{systemPrompt !== originalSystemPrompt && <small>unsaved changes</small>}</h2>
                    <span className="st31-cr">
                      <button type="button" className="st-btn st-ghost st-sm" onClick={() => setShowPromptConfig(false)} data-testid="toggle-prompt-config">Hide</button>
                    </span>
                  </div>
                  <div className="st31-cb">
                    <span className="st31-cap">This prompt shapes every answer the AI gives, for every church. Changes go live for everyone.</span>
                    {loadingPrompt ? (
                      <div className="st31-skstat">
                        {[0, 1, 2].map(i => <span key={i} className="st31-sk" style={{ height: 12, width: `${96 - i * 12}%` }} />)}
                      </div>
                    ) : (
                      <Textarea
                        value={systemPrompt}
                        onChange={(e) => setSystemPrompt(e.target.value)}
                        placeholder="Enter the AI system prompt..."
                        className="min-h-[400px] font-mono text-sm"
                        data-testid="system-prompt-textarea"
                      />
                    )}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span className="st31-cap st31-num">{systemPrompt.length.toLocaleString()} characters</span>
                      <span className="st31-fsp" />
                      {isCustomPrompt && (
                        <button type="button" className="st-lnk" onClick={() => setSystemPrompt(defaultSystemPrompt)}>View the default prompt</button>
                      )}
                    </div>
                  </div>
                  <div className="st31-cf" style={{ borderTop: '1px solid var(--st-line)', paddingTop: 12 }}>
                    {isCustomPrompt && (
                      <button type="button" className="st-btn st-ghost st-sm" onClick={handleResetSystemPrompt} disabled={savingPrompt} data-testid="reset-prompt-btn" style={{ color: 'var(--st-bad)' }}>
                        <RotateCcw /> Reset to default
                      </button>
                    )}
                    <span className="st31-fsp" />
                    <button
                      type="button"
                      className={`st-btn st-sm ${systemPrompt !== originalSystemPrompt && systemPrompt.length >= 100 ? 'st-pri' : 'st-sec'}`}
                      onClick={handleSaveSystemPrompt}
                      disabled={savingPrompt || systemPrompt === originalSystemPrompt || systemPrompt.length < 100}
                      data-testid="save-prompt-btn"
                    >
                      {savingPrompt ? <Loader2 className="st31-spinning" /> : <Save />} Save for all churches
                    </button>
                  </div>
                </>)}
              </div>

              {/* Articles */}
              <div className="st31-lblsec">Articles<span>{total.toLocaleString()} indexed</span></div>
              <div className="st31-card">
                {loading ? (
                  <div>
                    {[0, 1, 2, 3, 4].map(i => (
                      <div key={i} className="st31-arow">
                        <span className="st31-sk" style={{ width: 16, height: 16, borderRadius: 4, marginTop: 2 }} />
                        <span className="st31-c st31-skstat">
                          <span className="st31-sk" style={{ width: '52%', height: 14 }} />
                          <span className="st31-sk" style={{ width: '92%', height: 12 }} />
                          <span className="st31-sk" style={{ width: '72%', height: 12 }} />
                        </span>
                      </div>
                    ))}
                  </div>
                ) : articles.length === 0 ? (
                  <div className="st-empty" style={{ padding: '40px 24px' }}>
                    <span className="st-glyph">{searchQuery ? <Search /> : <BookOpen />}</span>
                    <b>{searchQuery ? `No article matches “${searchQuery}”` : 'No articles yet'}</b>
                    <span>{searchQuery ? 'Try another word, or clear the search.' : 'Import the WordPress file or add the first article by hand.'}</span>
                    {searchQuery ? (
                      <button type="button" className="st-btn st-sec st-sm" style={{ marginTop: 10 }} onClick={() => setSearchQuery('')}>Clear search</button>
                    ) : (
                      <button
                        type="button"
                        className="st-btn st-sec st-sm"
                        style={{ marginTop: 10 }}
                        onClick={() => { setFormData({ title: '', content: '', url: '', categories: '' }); setShowAddDialog(true); }}
                      >
                        <Plus /> Add article
                      </button>
                    )}
                  </div>
                ) : articles.map((article, index) => (
                  <div key={article.url || index} className="st31-arow">
                    <FileText className="st31-ic" />
                    <span className="st31-c">
                      <span className="st31-t">{article.title}</span>
                      <span className="st31-x">{article.content}...</span>
                      <span className="st31-m">
                        {article.source === 'manual_entry' && <span className="st-chip st-tb-xs">Manual</span>}
                        {article.categories?.length > 0 && <span>{article.categories.slice(0, 3).join(', ')}</span>}
                      </span>
                    </span>
                    <span className="st31-tr">
                      <button type="button" className="st-ibtn" style={{ width: 28, height: 28 }} onClick={() => handleViewArticle(article)} aria-label="View article"><ExternalLink /></button>
                      <button type="button" className="st-ibtn" style={{ width: 28, height: 28 }} onClick={() => handleEditArticle(article)} aria-label="Edit article"><Edit3 /></button>
                      <button type="button" className="st-ibtn" style={{ width: 28, height: 28 }} onClick={() => handleDeleteArticle(article)} aria-label="Delete article"><Trash2 /></button>
                    </span>
                  </div>
                ))}
                {totalPages > 1 && (
                  <div className="st31-pgbar">
                    <span className="st31-pgn">
                      {articles.length
                        ? `Showing ${(page - 1) * articles.length + 1}–${(page - 1) * articles.length + articles.length} of ${(total || 0).toLocaleString()}`
                        : `Page ${page} of ${totalPages}`}
                    </span>
                    <span style={{ display: 'flex', gap: 6 }}>
                      <button type="button" className="st-ibtn" style={{ width: 30, height: 30 }} onClick={() => fetchArticles(page - 1, searchQuery)} disabled={page === 1} aria-label="Previous page"><ChevronLeft /></button>
                      <button type="button" className="st-ibtn" style={{ width: 30, height: 30 }} onClick={() => fetchArticles(page + 1, searchQuery)} disabled={page === totalPages} aria-label="Next page"><ChevronRight /></button>
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
        {studioDialogs}
      </div>
      </SlotProvider>
    );
  }

  return (
    <div className="h-screen bg-gradient-to-br from-slate-50 dark:from-[#1e1f22] to-slate-100 dark:to-[#2d2f33] flex flex-col overflow-hidden">
      {/* Header */}
      <header className="bg-white/80 dark:bg-[#2d2f33]/80 backdrop-blur-sm border-b border-slate-200 dark:border-white/10 sticky top-0 z-20">
        <div className="px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={onClose} className="h-9 w-9">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center shadow-md shadow-purple-500/25">
                <Database className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-slate-800 dark:text-slate-100">AI Knowledge Base</h1>
                <p className="text-xs text-slate-500 dark:text-slate-400">{total} articles</p>
              </div>
            </div>
          </div>
        </div>
        
        {/* Tabs */}
        <div className="px-4 border-t border-slate-100 dark:border-white/10">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="bg-transparent border-0 p-0 h-auto gap-0">
              <TabsTrigger 
                value="articles" 
                className="relative px-4 py-3 rounded-none border-b-2 border-transparent data-[state=active]:border-purple-600 data-[state=active]:bg-transparent data-[state=active]:shadow-none bg-transparent font-medium text-slate-600 dark:text-slate-300 data-[state=active]:text-purple-600"
                data-testid="articles-tab"
              >
                <FileText className="w-4 h-4 mr-2" />
                Articles
              </TabsTrigger>
              <TabsTrigger 
                value="songs" 
                className="relative px-4 py-3 rounded-none border-b-2 border-transparent data-[state=active]:border-emerald-600 data-[state=active]:bg-transparent data-[state=active]:shadow-none bg-transparent font-medium text-slate-600 dark:text-slate-300 data-[state=active]:text-emerald-600"
                data-testid="songs-tab"
              >
                <Music className="w-4 h-4 mr-2" />
                Songs by Theme
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      </header>

      {/* Songs by Theme Tab Content */}
      {activeTab === 'songs' && (
        <div className="flex-1 overflow-y-auto p-4">
          <SongsByThemeManager />
        </div>
      )}

      {/* Articles Tab Content */}
      {activeTab === 'articles' && (
        <>
          {/* Action Buttons for Articles */}
          <div className="px-4 py-2 bg-card border-b border-slate-100 dark:border-white/10 flex items-center justify-end gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleReimport}
              disabled={reimporting}
              className="gap-2"
            >
              {reimporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              Re-import XML
            </Button>
            <Button
              size="sm"
              onClick={() => {
                setFormData({ title: '', content: '', url: '', categories: '' });
                setShowAddDialog(true);
              }}
              className="gap-2 bg-purple-600 hover:bg-purple-700"
            >
              <Plus className="w-4 h-4" />
              Add Article
            </Button>
          </div>

          {/* Search Bar */}
          <div className="px-4 py-3 bg-card border-b border-slate-100 dark:border-white/10">
            <div className="max-w-2xl mx-auto relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 dark:text-slate-500" />
              <Input
                type="text"
                placeholder="Search articles by title or content..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-slate-50 dark:bg-[#1e1f22] border-slate-200 dark:border-white/10"
              />
            </div>
          </div>

          {/* Scrollable Content Area - contains RSS, System Prompt, and Articles */}
          <div className="flex-1 overflow-y-auto">
        {/* RSS Configuration Section */}
        <div className="px-4 py-3 bg-gradient-to-r from-orange-50 to-amber-50 dark:from-orange-500/10 dark:to-amber-500/10 border-b border-amber-200 dark:border-orange-500/30">
          <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500 to-amber-600 flex items-center justify-center shadow-md shadow-orange-500/25">
                <Rss className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="font-semibold text-slate-800 dark:text-slate-100">RSS Feed Configuration</h2>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  {rssConfig.last_fetch 
                    ? `Last fetch: ${new Date(rssConfig.last_fetch).toLocaleString()} (${rssConfig.last_fetch_count} new articles)`
                    : 'Never fetched'}
                </p>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowRssConfig(!showRssConfig)}
              className="gap-2"
              data-testid="toggle-rss-config"
            >
              {showRssConfig ? 'Hide' : 'Configure'}
            </Button>
          </div>
          
          {showRssConfig && (
            <div className="mt-4 bg-card rounded-xl border border-slate-200 dark:border-white/10 p-4 shadow-sm">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    RSS Feed URL
                    {rssUrlInput !== rssConfig.rss_url && (
                      <span className="text-xs text-amber-600 dark:text-amber-400 font-normal">(unsaved changes)</span>
                    )}
                  </Label>
                  <div className="flex gap-2">
                    <Input
                      type="url"
                      value={rssUrlInput}
                      onChange={(e) => setRssUrlInput(e.target.value)}
                      placeholder="https://feeds.feedburner.com/..."
                      className="flex-1"
                      data-testid="rss-url-input"
                    />
                    <Button
                      onClick={handleSaveRssUrl}
                      disabled={savingRss || rssUrlInput === rssConfig.rss_url}
                      className="gap-2 bg-orange-600 hover:bg-orange-700"
                      data-testid="save-rss-url-btn"
                    >
                      {savingRss ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                      Save
                    </Button>
                  </div>
                  {rssConfig.default_url && rssUrlInput !== rssConfig.default_url && (
                    <p className="text-xs text-slate-400 dark:text-slate-500">
                      Default: {rssConfig.default_url}
                      <button 
                        onClick={() => setRssUrlInput(rssConfig.default_url)}
                        className="ml-2 text-orange-600 dark:text-orange-400 hover:underline"
                      >
                        Reset to default
                      </button>
                    </p>
                  )}
                </div>

                <div className="flex items-center justify-between pt-2 border-t border-slate-100 dark:border-white/10">
                  <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
                    <Clock className="w-4 h-4" />
                    <span>RSS feeds are automatically fetched monthly</span>
                  </div>
                  <Button
                    onClick={handleFetchRss}
                    disabled={fetchingRss}
                    variant="outline"
                    className="gap-2"
                    data-testid="fetch-rss-btn"
                  >
                    {fetchingRss ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                    Fetch Now
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

        {/* System Prompt Configuration Section */}
        <div className="px-4 py-3 bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-500/10 dark:to-indigo-500/10 border-b border-purple-200 dark:border-purple-500/30">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center shadow-md shadow-purple-500/25">
                  <Bot className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="font-semibold text-slate-800 dark:text-slate-100">AI System Prompt</h2>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    {isCustomPrompt 
                      ? `Custom prompt active${promptUpdatedAt ? ` (updated ${new Date(promptUpdatedAt).toLocaleDateString()})` : ''}`
                      : 'Using default prompt'}
                  </p>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowPromptConfig(!showPromptConfig)}
                className="gap-2"
                data-testid="toggle-prompt-config"
              >
                {showPromptConfig ? 'Hide' : 'Edit Prompt'}
              </Button>
            </div>
          
            {showPromptConfig && (
              <div className="mt-4 bg-card rounded-xl border border-slate-200 dark:border-white/10 p-4 shadow-sm">
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      Base System Prompt
                      {systemPrompt !== originalSystemPrompt && (
                        <span className="text-xs text-purple-600 dark:text-purple-400 font-normal">(unsaved changes)</span>
                    )}
                  </Label>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mb-2">
                    This prompt controls how the AI responds to ALL churches. Changes here affect every user.
                  </p>
                  {loadingPrompt ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-purple-500" />
                    </div>
                  ) : (
                    <Textarea
                      value={systemPrompt}
                      onChange={(e) => setSystemPrompt(e.target.value)}
                      placeholder="Enter the AI system prompt..."
                      className="min-h-[400px] font-mono text-sm"
                      data-testid="system-prompt-textarea"
                    />
                  )}
                  <div className="flex items-center justify-between text-xs text-slate-400 dark:text-slate-500">
                    <span>{systemPrompt.length.toLocaleString()} characters</span>
                    {isCustomPrompt && (
                      <button 
                        onClick={() => setSystemPrompt(defaultSystemPrompt)}
                        className="text-purple-600 dark:text-purple-400 hover:underline"
                      >
                        View default prompt
                      </button>
                    )}
                  </div>
                </div>

                <div className="flex items-center justify-between pt-2 border-t border-slate-100 dark:border-white/10">
                  <div className="flex items-center gap-2">
                    {isCustomPrompt && (
                      <Button
                        onClick={handleResetSystemPrompt}
                        disabled={savingPrompt}
                        variant="outline"
                        size="sm"
                        className="gap-2 text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-200 hover:bg-red-50 dark:hover:bg-red-500/15"
                        data-testid="reset-prompt-btn"
                      >
                        <RotateCcw className="w-4 h-4" />
                        Reset to Default
                      </Button>
                    )}
                  </div>
                  <Button
                    onClick={handleSaveSystemPrompt}
                    disabled={savingPrompt || systemPrompt === originalSystemPrompt || systemPrompt.length < 100}
                    className="gap-2 bg-purple-600 hover:bg-purple-700"
                    data-testid="save-prompt-btn"
                  >
                    {savingPrompt ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    Save for All Churches
                  </Button>
                </div>
              </div>
            </div>
          )}
          </div>
        </div>

        {/* Articles List */}
        <div className="p-4">
          <div className="max-w-4xl mx-auto space-y-3">
            {loading ? (
              <div className="text-center py-12">
                <Loader2 className="w-8 h-8 animate-spin mx-auto text-purple-500" />
                <p className="text-slate-500 dark:text-slate-400 mt-2">Loading articles...</p>
              </div>
            ) : articles.length === 0 ? (
              <div className="text-center py-12">
                <BookOpen className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                <p className="text-slate-500 dark:text-slate-400">No articles found</p>
                {searchQuery && (
                  <Button
                    variant="link"
                    onClick={() => setSearchQuery('')}
                    className="text-purple-600 dark:text-purple-400 mt-2"
                  >
                    Clear search
                  </Button>
                )}
              </div>
            ) : (
              articles.map((article, index) => (
                <div
                  key={article.url || index}
                  className="bg-card rounded-xl border border-slate-200 dark:border-white/10 p-4 hover:shadow-md transition-all"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <FileText className="w-4 h-4 text-purple-500 flex-shrink-0" />
                        <h3 className="font-semibold text-slate-800 dark:text-slate-100 truncate">{article.title}</h3>
                      </div>
                      <p className="text-sm text-slate-500 dark:text-slate-400 line-clamp-2 mb-2">
                        {article.content}...
                      </p>
                      <div className="flex items-center gap-3 text-xs text-slate-400 dark:text-slate-500">
                        {article.source === 'manual_entry' && (
                          <span className="px-2 py-0.5 bg-purple-100 dark:bg-purple-900/50 text-purple-700 dark:text-purple-300 rounded-full">Manual</span>
                        )}
                        {article.categories?.length > 0 && (
                          <span className="truncate">{article.categories.slice(0, 3).join(', ')}</span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleViewArticle(article)}
                        className="h-8 w-8 text-slate-400 dark:text-slate-500 hover:text-purple-600"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleEditArticle(article)}
                        className="h-8 w-8 text-slate-400 dark:text-slate-500 hover:text-blue-600"
                      >
                        <Edit3 className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDeleteArticle(article)}
                        className="h-8 w-8 text-slate-400 dark:text-slate-500 hover:text-red-600"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="border-t border-slate-200 dark:border-white/10 bg-card px-4 py-3">
          <div className="flex items-center justify-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => fetchArticles(page - 1, searchQuery)}
              disabled={page === 1}
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="text-sm text-slate-600 dark:text-slate-300 px-3">
              Page {page} of {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => fetchArticles(page + 1, searchQuery)}
              disabled={page === totalPages}
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
        </>
      )}

      {studioDialogs}
    </div>
  );
}

export default KnowledgeBaseManager;
